import React, { useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'

import { CustomPopup } from './CustomPopup'
import { ModalFooterButton } from './styles'
import { FeedbotSelect } from '../design-system/selects'
import { useSelectPortalRef } from '../SelectPortalProvider'
import { DateTimePicker } from '../design-system/DateTimePicker/DateTimePicker'

interface Props {
  onClose: () => void
  onConfirm: (dateFrom?: string, dateTo?: string) => void
}

export const DateTimeRangePickerPopup: React.FC<Props> = ({ onClose, onConfirm }) => {
  const portalRef = useSelectPortalRef()
  const [preset, setPreset] = useState<string | undefined>(undefined)
  const [dateTo, setDateTo] = useState<string | undefined>(undefined)
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined)

  const defaultOption = { label: 'Today', value: '0' }
  const getDateRangeForSelectedDaysBackInPast = (daysBack: number) => {
    return {
      dateTo: moment(Date.now()).format('YYYY-MM-DD HH:mm:ss'),
      dateFrom: moment().subtract(daysBack, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')
    }
  }

  return (
    <CustomPopup
      title="Choose time period for download"
      onClose={onClose}
      modalContentStyle={{ overflow: 'hidden' }}
      modalStyle={{ content: { height: preset === 'custom' ? '300px' : '250px', width: '500px' } }}
      footer={
        <>
          <ModalFooterButton label="Close" onClick={onClose} />
          <ModalFooterButton
            label="Ok"
            emphasis="primary"
            onClick={() => {
              if (dateFrom && dateTo) {
                onConfirm(dateFrom, dateTo)
                return
              }

              if (preset === 'custom') {
                onConfirm()
                return
              }

              const defaultDaysBack = Number(defaultOption.value)
              const defaultDateRange = getDateRangeForSelectedDaysBackInPast(defaultDaysBack)
              onConfirm(defaultDateRange.dateFrom, defaultDateRange.dateTo)
            }}
          />
        </>
      }
    >
      <Content>
        <FeedbotSelect
          menuPortalTarget={portalRef}
          defaultValue={defaultOption}
          options={[
            { label: 'Today', value: '0' },
            { label: 'Yesterday', value: '1' },
            { label: 'Last 3 days', value: '3' },
            { label: 'Last 7 days', value: '7' },
            { label: 'Last 14 days', value: '14' },
            { label: 'Last 30 days', value: '30' },
            { label: 'Custom', value: 'custom' }
          ]}
          onChange={option => {
            const selectedValue = option?.value
            setPreset(selectedValue)
            if (selectedValue === 'custom') {
              setDateTo(undefined)
              setDateFrom(undefined)
              return
            }

            const dateRange = getDateRangeForSelectedDaysBackInPast(Number(selectedValue))
            setDateTo(dateRange.dateTo)
            setDateFrom(dateRange.dateFrom)
          }}
        />
        {preset === 'custom' && (
          <CustomRange>
            <DatetimePicker type="date" value={dateFrom} onChange={setDateFrom} />
            <Spacer />
            -
            <Spacer />
            <DatetimePicker type="date" value={dateTo} onChange={setDateTo} />
          </CustomRange>
        )}
      </Content>
    </CustomPopup>
  )
}

const DatetimePicker = styled(DateTimePicker)`
  width: 100%;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
`

const CustomRange = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 15px;
`
const Spacer = styled.div`
  width: 10px;
`
